/** Web push on this device: subscribe, unsubscribe and read the current state. */
import { isNative } from '../platform/native';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// Web push only in browsers: the iOS/Android apps will use native push (APNs/FCM) instead.
export const pushSupported = (): boolean =>
  !isNative && typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export type DevicePushState = 'unsupported' | 'blocked' | 'on' | 'off';

export async function getDevicePushState(): Promise<DevicePushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return sub && Notification.permission === 'granted' ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

/** Asks for permission (if needed) and subscribes this browser; null when refused/unsupported. */
export async function registerSwAndSubscribe(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;
  const publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
  const appServerKey = publicKey ? urlBase64ToUint8Array(publicKey) : undefined;
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey });
}

/** Drops this browser's push subscription (the server row is removed separately). */
export async function unsubscribeDevice(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) await sub.unsubscribe();
  } catch { /* nothing to drop */ }
}
